import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import {
  getStudyHome,
  STUDY_HOME_QUERY_KEY,
  getStudyToolAccess,
  studyToolAccessQueryKey,
} from './studyApi';
import { useStudyAvailability } from './studyAvailability';
import { useStudyToolRegistry } from './studyToolRegistry';
import {
  contextualTools,
  rememberLearningTool,
  type LearningToolContext,
} from './studyLearningToolSuggestions';

/** A small optional next step beside the actual content, never an automatic tool/AI execution. */
export function StudyContextTools({
  context,
  disabled = false,
  compact = false,
}: {
  context: Omit<LearningToolContext, 'returnTo'> & { returnTo?: string };
  disabled?: boolean;
  compact?: boolean;
}) {
  const owner = useAuthStore(() => getKidSub());
  const location = useLocation(),
    navigate = useNavigate();
  const home = useQuery({
    queryKey: [...STUDY_HOME_QUERY_KEY, 'context-tools', owner],
    queryFn: async () => {
      const value = await getStudyHome();
      if (getKidSub() !== owner) throw new Error('The learner changed.');
      return value;
    },
    enabled: Boolean(owner),
    retry: false,
  });
  const access = useQuery({
    queryKey: studyToolAccessQueryKey(owner),
    queryFn: getStudyToolAccess,
    enabled: Boolean(owner),
    retry: false,
  });
  const availability = useStudyAvailability(Boolean(owner));
  const registry = useStudyToolRegistry();
  const full = { ...context, returnTo: context.returnTo ?? location.pathname + location.search };
  const scope = JSON.stringify([
    owner,
    full.subjectKey,
    full.returnTo,
    full.label,
    full.sourceVersion,
    full.text,
  ]);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const suggestions = contextualTools(full, {
    access: access.data,
    availability: availability.data,
    registry: registry.data,
    year: availability.data?.year_level ?? home.data?.academic_year?.year_level ?? null,
  });
  if (!owner || dismissed === scope || !suggestions.length) return null;
  const content = (
    <>
      <ul className="space-y-3">
        {suggestions.map(({ tool, reason }) => (
          <li key={tool.id}>
            <p className="font-bold text-ink">{tool.name}</p>
            <p className="mt-1 text-sm leading-relaxed text-ink-soft">{reason}</p>
            <button
              type="button"
              disabled={disabled}
              className="btn-pill-secondary mt-2 min-h-11 disabled:opacity-50"
              onClick={() => {
                if (getKidSub() !== owner) return;
                const key = rememberLearningTool(owner, tool.path, full);
                if (key) {
                  const params = new URLSearchParams({ learningContext: key });
                  const year =
                    availability.data?.year_level ?? home.data?.academic_year?.year_level;
                  if (year) params.set('year', String(year));
                  navigate(`${tool.path}?${params}`);
                }
              }}
            >
              Explore with this tool
            </button>
          </li>
        ))}
      </ul>
      {disabled && (
        <p role="status" className="mt-2 text-sm text-ink-soft">
          Save or check your current work before opening another tool.
        </p>
      )}
      <p className="mt-2 text-xs text-slate2">
        Opens an Explore model. Your work stays separate; no AI starts and no Stars are spent.
      </p>
    </>
  );
  const dismiss = (
    <button
      type="button"
      className="btn-pill-ghost min-h-11 text-sm"
      onClick={() => setDismissed(scope)}
    >
      Not now
    </button>
  );
  return (
    <aside
      className={
        compact
          ? 'my-3 border-y border-hairline text-sm'
          : 'my-4 rounded-2xl border border-hairline bg-wash-sky p-4'
      }
      aria-label="Tools for this learning step"
    >
      {compact ? (
        <details>
          <summary className="min-h-11 cursor-pointer py-3 font-bold text-ink">
            Explore this idea with {suggestions[0].tool.name}
          </summary>
          <div className="pb-3">
            {content}
            {dismiss}
          </div>
        </details>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-extrabold text-ink">A tool can help you explore this</h3>
            {dismiss}
          </div>
          {content}
        </>
      )}
    </aside>
  );
}
